import React, {
    useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import SpecDropdownAddOptionModal from './SpecDropdownAddOptionModal';
import {
    formatSpecDropdownMultiValue,
    getSpecDropdownSelectionState,
    SPEC_DROPDOWN_VALUE_SEP,
} from '../utils/specDropdownValue';

/**
 * 사양 제목별 서버에 등록된 옵션으로 드롭다운 표시 (다중 선택).
 * DB에 없는 기존 값은 "(기존값)" 옵션으로 유지.
 */
const SpecDropdownSelect = ({
    specTitle,
    value,
    onChange,
    options = [],
    disabled = false,
    className = '',
    allowAddOption = false,
    onAddDropdownOption,
}) => {
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [panelRect, setPanelRect] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addBusy, setAddBusy] = useState(false);

    const sorted = useMemo(() => {
        const forTitle = options.filter((o) => o.specTitle === specTitle);
        return [...forTitle].sort((a, b) => (
            (a.sortOrder || 0) - (b.sortOrder || 0)
            || String(a.optionValue || '').localeCompare(String(b.optionValue || ''), 'ko')
        ));
    }, [options, specTitle]);

    const optionValues = useMemo(() => new Set(sorted.map((o) => o.optionValue)), [sorted]);
    const raw = value == null ? '' : String(value);

    const selectionState = useMemo(
        () => getSpecDropdownSelectionState(raw, optionValues),
        [raw, optionValues],
    );

    const selectedSet = useMemo(
        () => new Set(selectionState.selected),
        [selectionState.selected],
    );

    const legacyWhole = selectionState.legacyWhole;
    const hasLegacy = legacyWhole != null && legacyWhole !== '';

    const q = searchQuery.trim().toLowerCase();
    const filteredSorted = useMemo(() => {
        if (!q) {
            return sorted;
        }
        return sorted.filter((o) => String(o.optionValue || '').toLowerCase().includes(q));
    }, [sorted, q]);

    const showLegacyRow = hasLegacy && (
        !q
        || legacyWhole.toLowerCase().includes(q)
        || '(기존값)'.includes(q)
        || `기존값 ${legacyWhole}`.toLowerCase().includes(q)
    );

    const canShowAdd = allowAddOption && typeof onAddDropdownOption === 'function';

    const displayLabel = useMemo(() => {
        if (legacyWhole) {
            return legacyWhole;
        }
        if (selectionState.selected.length === 0) {
            return '선택';
        }
        return selectionState.selected.join(SPEC_DROPDOWN_VALUE_SEP);
    }, [legacyWhole, selectionState.selected]);

    const updatePanelRect = () => {
        const el = triggerRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        setPanelRect({
            top: r.bottom + 4,
            left: r.left,
            width: Math.max(r.width, 200),
        });
    };

    useLayoutEffect(() => {
        if (!open) {
            setPanelRect(null);
            return undefined;
        }
        updatePanelRect();
        window.addEventListener('resize', updatePanelRect);
        window.addEventListener('scroll', updatePanelRect, true);
        return () => {
            window.removeEventListener('resize', updatePanelRect);
            window.removeEventListener('scroll', updatePanelRect, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onDocMouseDown = (e) => {
            if (addModalOpen) {
                return;
            }
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open, addModalOpen]);

    const applySelection = (nextValues, { closePanel = false } = {}) => {
        onChange(formatSpecDropdownMultiValue(nextValues));
        if (closePanel) {
            setOpen(false);
            setSearchQuery('');
        }
    };

    const clearAll = () => {
        applySelection([], { closePanel: true });
    };

    const toggleOption = (optionValue) => {
        const next = new Set(selectedSet);
        if (legacyWhole) {
            next.clear();
        }
        if (next.has(optionValue)) {
            next.delete(optionValue);
        } else {
            next.add(optionValue);
        }
        applySelection([...next]);
    };

    const toggleLegacy = () => {
        if (legacyWhole && selectedSet.size === 0) {
            clearAll();
            return;
        }
        applySelection([legacyWhole]);
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }
        setOpen((prev) => {
            const next = !prev;
            if (next) {
                setSearchQuery('');
            }
            return next;
        });
    };

    const openAddModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAddModalOpen(true);
    };

    const handleAddConfirm = async (inputValue) => {
        const trimmed = String(inputValue || '').trim();
        if (!trimmed || !onAddDropdownOption) {
            return;
        }
        setAddBusy(true);
        try {
            const result = await onAddDropdownOption(specTitle, trimmed);
            const added = String(result?.optionValue ?? trimmed).trim();
            const next = new Set(legacyWhole ? [] : selectedSet);
            next.add(added);
            applySelection([...next], { closePanel: true });
            setAddModalOpen(false);
        } finally {
            setAddBusy(false);
        }
    };

    const triggerClasses = ['spec-dropdown-trigger', className].filter(Boolean).join(' ');
    const empty = filteredSorted.length === 0 && !showLegacyRow && q !== '';
    const selectedCount = selectionState.selected.length + (legacyWhole ? 1 : 0);

    const panelStyle = panelRect
        ? {
            position: 'fixed',
            top: panelRect.top,
            left: panelRect.left,
            width: panelRect.width,
            zIndex: 5500,
        }
        : { display: 'none' };

    return (
        <div className="spec-dropdown-root" ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClasses}
                onClick={toggleOpen}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <span className="spec-dropdown-trigger-label" title={displayLabel}>{displayLabel}</span>
                <span className="spec-dropdown-trigger-chevron" aria-hidden>▾</span>
            </button>
            {open && (
                <div className="spec-dropdown-panel spec-dropdown-panel--multi" style={panelStyle} role="listbox" aria-multiselectable="true">
                    <p className="spec-dropdown-multi-hint">여러 항목을 선택할 수 있습니다. 클릭으로 선택·해제합니다.</p>
                    <input
                        type="text"
                        className="spec-dropdown-search field-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="옵션 검색…"
                        autoComplete="off"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {selectedCount > 0 && (
                        <div className="spec-dropdown-selected-summary">
                            <span className="spec-dropdown-selected-count">{selectedCount}개 선택</span>
                            <button type="button" className="spec-dropdown-clear-all" onClick={clearAll}>전체 해제</button>
                        </div>
                    )}
                    <ul className="spec-dropdown-list">
                        {showLegacyRow && (
                            <li>
                                <button
                                    type="button"
                                    className={`spec-dropdown-option spec-dropdown-option-legacy${legacyWhole && selectedSet.size === 0 ? ' is-selected' : ''}`}
                                    onClick={toggleLegacy}
                                >
                                    <span className="spec-dropdown-option-check" aria-hidden>{legacyWhole && selectedSet.size === 0 ? '☑' : '☐'}</span>
                                    <span>{`(기존값) ${legacyWhole}`}</span>
                                </button>
                            </li>
                        )}
                        {filteredSorted.map((o) => (
                            <li key={o.id}>
                                <button
                                    type="button"
                                    className={`spec-dropdown-option${selectedSet.has(o.optionValue) ? ' is-selected' : ''}`}
                                    onClick={() => toggleOption(o.optionValue)}
                                >
                                    <span className="spec-dropdown-option-check" aria-hidden>{selectedSet.has(o.optionValue) ? '☑' : '☐'}</span>
                                    <span>{o.optionValue}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    {empty && (
                        <div className="spec-dropdown-empty">검색 결과가 없습니다.</div>
                    )}
                    {canShowAdd && (
                        <div className="spec-dropdown-add-wrap">
                            <button
                                type="button"
                                className="spec-dropdown-add-button"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={openAddModal}
                            >
                                + 추가하기
                            </button>
                        </div>
                    )}
                </div>
            )}
            <SpecDropdownAddOptionModal
                open={addModalOpen}
                specTitle={specTitle}
                onClose={() => !addBusy && setAddModalOpen(false)}
                onConfirm={handleAddConfirm}
                busy={addBusy}
            />
        </div>
    );
};

export default SpecDropdownSelect;
